import { useState, useEffect } from 'react'
import { makeZip, downloadZip } from 'client-zip'
import { brand, logoUrl, symbolUrl } from '@brand'

function formatSize(bytes) {
    if (!bytes && bytes !== 0) return '—'
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let i = 0, v = bytes
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
    return `${v.toFixed(i > 0 ? 1 : 0)} ${units[i]}`
}

function formatDate(iso) {
    if (!iso) return '—'
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

function FileRow({ file }) {
    const ext = file.name.split('.').pop()?.toUpperCase() || '?'
    return (
        <a
            href={file.url}
            download={file.name}
            style={{
                display: 'grid',
                gridTemplateColumns: '36px minmax(0,1fr) 80px 100px',
                alignItems: 'center',
                gap: 12,
                padding: '11px 24px',
                borderBottom: '0.5px solid rgba(0,0,0,0.06)',
                textDecoration: 'none',
                color: 'inherit',
                transition: 'background 0.1s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#f5f5f7'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
            <span style={{
                fontSize: 9, fontWeight: 700,
                fontFamily: 'ui-monospace, monospace',
                color: '#86868b', background: '#e8e8ed',
                borderRadius: 3, padding: '2px 4px', textAlign: 'center',
            }}>
                {ext}
            </span>
            <span style={{
                fontSize: 13, fontFamily: 'ui-monospace, monospace', color: '#1d1d1f',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
                {file.name}
            </span>
            <span style={{ fontSize: 12, color: '#86868b', fontFamily: 'ui-monospace, monospace' }}>
                {formatSize(file.size)}
            </span>
            <span style={{ fontSize: 12, color: '#007aff', fontWeight: 500, textAlign: 'right' }}>
                Download ↓
            </span>
        </a>
    )
}

// Client-side ZIP fallback — used while the server ZIP is still building.
// Chrome/Edge: showSaveFilePicker streams direct to disk, no RAM limit.
// Safari/Firefox: blob approach, fine for smaller shares.
function ClientZipButton({ files, zipName, label }) {
    const [state, setState]       = useState('idle')
    const [progress, setProgress] = useState(0)
    const [error, setError]       = useState(null)

    const canStream = 'showSaveFilePicker' in window && window.self === window.top

    async function handleClick() {
        setError(null)

        async function* entries() {
            for (const file of files) {
                const r = await fetch(file.url)
                if (!r.ok) throw new Error(`Failed to fetch ${file.name}`)
                yield { name: file.name, input: r, size: file.size }
                setProgress(p => p + 1)
            }
        }

        if (canStream) {
            let fileHandle
            try {
                fileHandle = await window.showSaveFilePicker({
                    suggestedName: zipName,
                    types: [{ description: 'ZIP archive', accept: { 'application/zip': ['.zip'] } }],
                })
            } catch (err) {
                if (err.name === 'AbortError') return
                setError('Could not open save dialog.')
                return
            }

            setState('zipping')
            setProgress(0)

            try {
                const writable = await fileHandle.createWritable()
                await makeZip(entries()).pipeTo(writable)
                setState('done')
                setTimeout(() => setState('idle'), 3000)
            } catch (err) {
                console.error('ZIP stream error:', err)
                setState('idle')
                setError('Download failed – please try again.')
            }
        } else {
            setState('zipping')
            setProgress(0)

            try {
                const blob = await downloadZip(entries()).blob()
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = zipName
                document.body.appendChild(a)
                a.click()
                a.remove()
                URL.revokeObjectURL(url)
                setState('done')
                setTimeout(() => setState('idle'), 3000)
            } catch (err) {
                console.error('ZIP blob error:', err)
                setState('idle')
                setError('Download failed – please try again.')
            }
        }
    }

    const btnLabel = {
        idle:    label || 'Download all as ZIP',
        zipping: progress > 0 ? `Zipping ${progress}/${files.length}…` : 'Preparing…',
        done:    'Downloaded ✓',
    }[state] || 'Download all as ZIP'

    const bg = state === 'done' ? '#34c759' : '#1d1d1f'

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            {error && (
                <div style={{ fontSize: 12, color: '#ff3b30', maxWidth: 280, textAlign: 'right' }}>
                    {error}
                </div>
            )}
            <button
                onClick={handleClick}
                disabled={state === 'zipping'}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: bg, color: '#fff',
                    padding: '9px 20px', borderRadius: 10,
                    fontSize: 13, fontWeight: 600,
                    border: 'none', cursor: state === 'zipping' ? 'default' : 'pointer',
                    opacity: state === 'zipping' ? 0.75 : 1,
                    transition: 'all 0.15s',
                }}
            >
                {btnLabel}
            </button>
        </div>
    )
}

export default function SharePage({ shareId }) {
    const [data, setData]       = useState(null)
    const [error, setError]     = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        fetch(`/api/share/${shareId}`)
            .then(async res => {
                const body = await res.json()
                if (!res.ok) setError({ ...body, code: body.code || 'ERROR' })
                else setData(body)
            })
            .catch(() => setError({ error: 'Failed to load share', code: 'NETWORK' }))
            .finally(() => setLoading(false))
    }, [shareId])

    const totalSize = data?.files?.reduce((sum, f) => sum + (f.size || 0), 0) || 0

    if (loading) return (
        <div style={pageStyle}>
            <div style={cardStyle}>
                <Logo />
                <p style={{ color: '#86868b', fontSize: 14, marginTop: 24, textAlign: 'center' }}>Loading…</p>
            </div>
        </div>
    )

    if (error) {
        const isExpired   = error.code === 'EXPIRED'
        const isNotFound  = error.code === 'NOT_FOUND'
        const isPreparing = error.code === 'PREPARING'
        return (
            <div style={pageStyle}>
                <div style={cardStyle}>
                    <Logo />
                    <div style={{
                        marginTop: 32, padding: '20px 24px', borderRadius: 12,
                        background: isPreparing ? '#f0f7ff' : isExpired ? '#fff8f0' : '#fff0f0',
                        border: `0.5px solid ${isPreparing ? '#b8d4f8' : isExpired ? '#ffd5a0' : '#ffcdd2'}`,
                    }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#1d1d1f', marginBottom: 6 }}>
                            {isPreparing ? 'Preparing your files…'
                                : isExpired ? 'This link has expired'
                                    : isNotFound ? 'Link not found'
                                        : 'Something went wrong'}
                        </div>
                        <div style={{ fontSize: 13, color: '#86868b' }}>
                            {isPreparing ? 'Your files are being uploaded. Check back in a few minutes.'
                                : isExpired ? 'This share link has passed its expiry date.'
                                    : isNotFound ? 'This share link is invalid or has been deleted.'
                                        : error.error || 'An unexpected error occurred.'}
                        </div>
                        {isPreparing && (
                            <button onClick={() => window.location.reload()} style={{
                                marginTop: 14, background: '#007aff', color: '#fff',
                                border: 'none', borderRadius: 8, padding: '7px 16px',
                                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            }}>Check again</button>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    const zipName = `${(data.name || shareId).replace(/[^a-zA-Z0-9_\-. ]/g, '_')}.zip`

    return (
        <div style={pageStyle}>
            <div style={{
                background: '#fff', borderRadius: 20,
                border: '0.5px solid rgba(0,0,0,0.1)',
                width: '100%', maxWidth: 680,
                overflow: 'hidden', display: 'flex', flexDirection: 'column',
                maxHeight: 'calc(100vh - 80px)',
            }}>
                <div style={{ padding: '32px 32px 24px', flexShrink: 0 }}>
                    <Logo />
                    <h1 style={{
                        fontSize: 22, fontWeight: 700, color: '#1d1d1f',
                        letterSpacing: '-0.02em', marginTop: 28, marginBottom: 6,
                    }}>
                        {data.name || 'Shared Files'}
                    </h1>
                    <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#86868b' }}>
                        <span>{data.files.length} file{data.files.length !== 1 ? 's' : ''}</span>
                        <span>{formatSize(totalSize)}</span>
                        <span>Expires {formatDate(data.expiresAt)}</span>
                    </div>
                </div>

                <div style={{
                    display: 'grid', gridTemplateColumns: '36px minmax(0,1fr) 80px 100px',
                    gap: 12, padding: '8px 24px',
                    fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase',
                    color: '#86868b',
                    borderTop: '0.5px solid rgba(0,0,0,0.07)',
                    borderBottom: '0.5px solid rgba(0,0,0,0.07)',
                    flexShrink: 0,
                }}>
                    <span /><span>File</span><span>Size</span><span />
                </div>

                <div style={{ overflowY: 'auto', flex: 1 }}>
                    {data.files.map((f, i) => <FileRow key={i} file={f} />)}
                </div>

                <div style={{
                    padding: '16px 24px',
                    borderTop: '0.5px solid rgba(0,0,0,0.07)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: '#f5f5f7', flexShrink: 0,
                }}>
                    <span style={{ fontSize: 12, color: '#86868b' }}>Shared by {brand.name}</span>

                    {data.hasZip && data.zipUrl
                        ? (
                            // Server ZIP ready — native link, resumable, works everywhere
                            <a
                                href={data.zipUrl}
                                download={zipName}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    background: '#1d1d1f', color: '#fff',
                                    padding: '9px 20px', borderRadius: 10,
                                    fontSize: 13, fontWeight: 600,
                                    textDecoration: 'none', transition: 'opacity 0.15s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                            >
                                Download all as ZIP
                            </a>
                        ) : data.buildingZip
                            ? (
                                // ZIP is being assembled — show status + client fallback
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                                    <span style={{ fontSize: 11, color: '#86868b' }}>Assembling ZIP…</span>
                                    <ClientZipButton files={data.files} zipName={zipName} />
                                </div>
                            ) : (
                                // No server ZIP yet — client-side fallback
                                <ClientZipButton files={data.files} zipName={zipName} />
                            )
                    }
                </div>
            </div>
        </div>
    )
}

function Logo() {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {(symbolUrl || logoUrl) && (
                <img
                    src={symbolUrl ?? logoUrl}
                    alt={brand.name}
                    style={{ width: 28, height: 28, borderRadius: 7, objectFit: 'cover', flexShrink: 0 }}
                />
            )}
            <span style={{ fontSize: 14, fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.01em' }}>
                {brand.name}
            </span>
        </div>
    )
}

const pageStyle = {
    minHeight: '100vh', background: '#f5f5f7',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '40px 20px',
    fontFamily: "-apple-system, 'SF Pro Display', BlinkMacSystemFont, sans-serif",
}

const cardStyle = {
    background: '#fff', borderRadius: 20,
    border: '0.5px solid rgba(0,0,0,0.1)',
    width: '100%', maxWidth: 480, padding: 40, textAlign: 'center',
}